/* ============================================================================
   cart-drawer.js — <nko-cart-drawer>, die Warenkorb-Schublade
   ----------------------------------------------------------------------------
   Ersetzt die HulkApps Slide Cart. Pflichtenheft: docs/cart-drawer-spec.md
   Bis M3 hiess die Datei nko-cart-drawer.js und lud im <head> jeder Seite.

   WANN DIESE DATEI LAEDT (M3, Architektur §2.3)
   ---------------------------------------------------------------------------
   sections/nko-cart-drawer.liquid bindet sie auf Produktvorlagen
   (`template.name == 'product'`) direkt als Modul ein — dort ist der Kauf
   das Ziel der Seite, die Schublade muss beim ersten `nko:cart:add` stehen.
   Auf allen anderen Seiten holt ein kleines Ladeskript in derselben Section
   sie erst per import(), wenn sie gebraucht wird: Zeiger oder Fokus auf dem
   Warenkorb-Symbol, Klick darauf, `nko:cart:add`, `?cart=open`/`#cart` in
   der Adresse oder die Rueckkehr aus dem bfcache. Was waehrend des Ladens
   passiert ist, legt es in `data-vorlauf` ab; connectedCallback arbeitet es
   ab (siehe dort).

   KEIN CLIENT-TEMPLATING, EINE ANFRAGE JE AKTION (P2-31)
   ---------------------------------------------------------------------------
   Jede Aenderung (add.js, change.js, update.js) schickt `sections` mit;
   Shopify liefert die Section dann in DERSELBEN Antwort neu gerendert
   zurueck (Bundled Section Rendering), und hier werden Koerper, Summe und
   Titel getauscht.
   Vorher folgte auf jede Aenderung ein zweites GET `?section_id=` — gemessen
   822 ms vom Kauf bis zur offenen Schublade, davon 234 ms nur fuer die zweite
   Runde. Das GET bleibt als Rueckfall (bfcache, Oeffnen ohne Aenderung,
   Ereignisse ohne mitgeliefertes HTML).

   ALLE AENDERUNGEN LAUFEN DURCH EINE WARTESCHLANGE (P2-20)
   ---------------------------------------------------------------------------
   Vorher schuetzte ein Riegel (`laeuft`) die Anfragen — und VERWARF, was
   waehrenddessen kam: Entfernen und Upsell-"Hinzufuegen" gingen still
   verloren, und `aktualisieren()` loeschte die Merkliste, sodass auch
   Mengenklicks waehrend einer laufenden Anfrage verschwanden. Jetzt haengt
   jede Aenderung hinten an eine Promise-Kette (`einreihen`) und laeuft, wenn
   die vorige fertig ist. Nichts wird verworfen.

   DAS BUENDELN DER MENGENKLICKS
   ---------------------------------------------------------------------------
   Die Anzeige zaehlt sofort, die Zielmenge landet in einer Merkliste, und erst
   450 ms nach dem LETZTEN Klick gehen die offenen Zeilen raus: je Zeile ein
   /cart/change.js (prueft den Bestand, update.js tut das nicht — siehe
   wartendeSenden), Entfernen gesammelt als EIN /cart/update.js. Die
   Merkliste bleibt bis zur Antwort stehen; was waehrenddessen dazukam, geht
   mit der naechsten Runde.

   WARUM DIE ZEILE UEBER DEN SCHLUESSEL GEHT, NICHT UEBER DEN INDEX
   ---------------------------------------------------------------------------
   `updates` nimmt Varianten-IDs oder ZEILENSCHLUESSEL (`item.key`). Der
   Schluessel ist auch dann eindeutig, wenn dieselbe Variante zweimal mit
   unterschiedlichen Eigenschaften im Warenkorb liegt — genau der Fall bei den
   Bundles und Konfigurator-Sets dieses Shops.

   OEFFNEN UEBER DIE ADRESSE (Entscheidung D-CART-PAGE)
   ---------------------------------------------------------------------------
   `?cart=open` oder `#cart` in der Adresse oeffnet die Schublade beim Laden;
   die Markierung wird danach aus der Adresse genommen (history.replaceState),
   damit Neuladen oder Zurueck sie nicht erneut aufklappt. /cart leitet schon
   im head auf die Startseite mit `?cart=open` um (layout/theme.liquid;
   templates/cart.liquid ist nur der Rueckfall ohne JavaScript).
   ========================================================================== */

const ROOT = window.Shopify?.routes?.root || "/";
const WURZEL = ROOT.endsWith("/") ? ROOT : `${ROOT}/`;

/* Dieselbe Liste wie in assets/popup.js — der Dialog dort ist die Vorlage
   fuer die Fokusfuehrung hier. */
const FOKUSSIERBAR = [
  "a[href]",
  "button:not([disabled])",
  "input:not([disabled]):not([type=hidden])",
  "select:not([disabled])",
  "textarea:not([disabled])",
  '[tabindex]:not([tabindex="-1"])',
].join(",");

/* Merkmale, an denen ein fokussiertes Element nach dem Tausch wiedergefunden
   wird (P2-30). Der Tausch ersetzt das Element; ohne das fiel der Fokus auf
   <body>, ausserhalb des Dialogs. */
const FOKUS_MERKMALE = [
  "data-nko-cart-qty",
  "data-nko-cart-remove",
  "data-nko-cart-add",
  "data-nko-cart-discount-remove",
  "data-nko-cart-discount",
  "id",
];

class NkoCartDrawer extends HTMLElement {
  connectedCallback() {
    this.sektion = this.getAttribute("data-section") || "";
    this.wartend = new Map();
    this.buendelTimer = null;
    this.kette = Promise.resolve();

    /* Vorlauf des Ladeskripts (sections/nko-cart-drawer.liquid): hier gelesen
       und entfernt, abgearbeitet am Ende dieser Methode. */
    const vorlauf = this.getAttribute("data-vorlauf");
    this.removeAttribute("data-vorlauf");

    /* Alle Hoerer an EINEM Signal (Architektur §5.1): disconnectedCallback
       nimmt sie mit einem Aufruf ab — auch die an der Schublade selbst, die
       ein erneutes Einhaengen sonst doppelt gebunden haette. */
    this.ac = new AbortController();
    const { signal } = this.ac;

    this.addEventListener("click", (e) => this.klick(e), { signal });
    this.addEventListener(
      "keydown",
      (e) => {
        if (
          e.key === "Enter" &&
          e.target.matches?.(".nko-cart__discount-input")
        ) {
          e.preventDefault();
          this.rabattAnwenden();
        }
      },
      { signal },
    );

    /* Das Warenkorb-Symbol im Header ist ein <a> auf /cart. Ohne JS bleibt es
       ein normaler Link — genau richtig. */
    document.addEventListener(
      "click",
      (e) => {
        const a = e.target.closest?.(".nav__item--cart");
        if (!a) return;
        e.preventDefault();
        this.oeffnenUndAktualisieren();
      },
      { signal },
    );

    document.addEventListener(
      "keydown",
      (e) => {
        if (!this.classList.contains("is-open")) return;
        if (e.key === "Escape") this.schliessen();
        else if (e.key === "Tab") this.falle(e);
      },
      { signal },
    );

    /* Legt die Produktseite etwas in den Warenkorb, geht die Schublade auf.
       assets/product.js und assets/configurator.js feuern dieses Ereignis nach
       erfolgreichem cart/add. Bringt es das gerenderte HTML schon mit
       (`detail.sections[<id dieser Section>]`, Bundled Section Rendering),
       wird das eingebaut — sonst selbst geholt.

       SCHEITERT DAS NACHLADEN, LIEGT DER ARTIKEL TROTZDEM IM WARENKORB — das
       Ereignis kommt erst nach erfolgreichem cart/add. Vorher ging die
       Schublade dann mit dem ALTEN Stand und der Meldung „Der Artikel konnte
       nicht in den Warenkorb gelegt werden“ auf; wer daraufhin noch einmal
       tippte, hatte ihn doppelt (Nachpruefung am Simulator, 06.10.2026, als
       die Section-Abfrage mit 404 antwortete). Jetzt laedt die Seite mit
       `?cart=open` neu: der Server rendert die Schublade mit dem echten
       Stand, adresseAuswerten() oeffnet sie. Keine Schleife — nach dem Laden
       gibt es kein Ereignis mehr. */
    this.nachAdd = (e) => {
      const html = e.detail?.sections?.[this.sektion];
      this.einreihen(() => (html ? this.einbauen(html) : this.aktualisieren()))
        .then(() => this.oeffnen())
        .catch(() => this.neuLadenOffen());
    };
    document.addEventListener("nko:cart:add", this.nachAdd, { signal });

    /* Zurueck aus dem bfcache zeigt die Seite den Warenkorb von damals —
       Zaehler, Positionen und die Mengen, von denen der naechste Klick aus
       rechnet (P2-31). Still neu holen. */
    window.addEventListener(
      "pageshow",
      (e) => {
        if (e.persisted)
          this.einreihen(() => this.aktualisieren()).catch(() => {});
      },
      { signal },
    );

    /* DIE SEITE HINTER DER SCHUBLADE BLEIBT STEHEN, AUCH MIT DER iOS-TASTATUR.

       `overflow: hidden` am <html> (cart-drawer.css) sperrt das Scrollen von
       Hand, nicht aber iOS selbst: geht fuer das Rabattfeld die Tastatur auf,
       schiebt Safari den sichtbaren Ausschnitt nach oben, bis das Feld frei
       steht, und zieht beim Einfahren der Tastatur die Seite dorthin nach.
       Gemessen auf dem iPhone 17: rund 200px — nach dem Schliessen der
       Schublade stand der Kunde an einer anderen Stelle als vorher.

       Verhindern laesst sich das Verschieben nicht (das Feld SOLL ueber der
       Tastatur stehen), also wird es zurueckgenommen: beim ersten Fokus auf
       ein Textfeld merkt sich die Schublade, wo die Seite steht, und
       lageHalten() stellt das wieder her, sobald sie sich bewegt. Der
       uebliche Kniff, <body> beim Oeffnen auf `position: fixed` zu setzen,
       haette bei JEDEM Oeffnen den Scrollstand auf 0 geworfen — der klebende
       Kopf und alles, was am Scrollen haengt (header.js), waere auch am
       Desktop umgesprungen. So passiert ohne Tastatur nichts. */
    this.addEventListener(
      "focusin",
      (e) => {
        if (this.lageY != null || !this.classList.contains("is-open")) return;
        if (e.target.matches?.("input, textarea")) this.lageY = window.scrollY;
      },
      { signal },
    );
    const lage = () => this.lageHalten();
    window.addEventListener("scroll", lage, { signal, passive: true });
    window.visualViewport?.addEventListener("resize", lage, { signal });

    /* Die geschlossene Schublade gehoert weder in die Tab-Reihenfolge noch in
       den Accessibility-Baum. Das Attribut steht auch im Markup, damit es
       schon vor dem ersten Skriptlauf gilt. */
    if (this.panel) this.panel.inert = true;

    this.adresseAuswerten();

    /* VORLAUF (M3). Ausserhalb der Produktvorlagen laedt diese Datei erst bei
       Bedarf. Kam der Anlass, bevor sie da war, steht er in `data-vorlauf`
       (gesetzt vom Ladeskript in sections/nko-cart-drawer.liquid), und hier
       passiert nachtraeglich, was die Hoerer oben getan haetten:
         oeffnen        Klick auf das Warenkorb-Symbol
         add            `nko:cart:add` (ohne das mitgelieferte HTML — die
                        Section wird per GET geholt, wie bei einem Ereignis
                        ohne `sections`)
         aktualisieren  Rueckkehr aus dem bfcache
       `?cart=open`/`#cart` braucht keinen Vorlauf, das erledigt
       adresseAuswerten() oben. */
    if (vorlauf === "add") this.nachAdd(new CustomEvent("nko:cart:add"));
    else if (vorlauf === "oeffnen") this.oeffnenUndAktualisieren();
    else if (vorlauf === "aktualisieren")
      this.einreihen(() => this.aktualisieren()).catch(() => {});
  }

  disconnectedCallback() {
    this.ac?.abort();
    clearTimeout(this.inertTimer);
    clearTimeout(this.buendelTimer);
    /* Ersetzt der Theme-Editor die OFFENE Schublade, bliebe sonst die
       Scrollsperre am <html> stehen, obwohl das neue Element zu ist. (Das
       Oeffnen beim Auswaehlen der Section im Editor steht in
       assets/editor.js, das nur der Editor laedt.) */
    if (this.classList.contains("is-open")) {
      document.documentElement.classList.remove("nko-cart-offen");
    }
  }

  /* Klick auf das Warenkorb-Symbol: sofort auf, dann still neu holen — ein
     anderes Skript kann den Warenkorb inzwischen geaendert haben. */
  oeffnenUndAktualisieren() {
    this.oeffnen();
    this.einreihen(() => this.aktualisieren()).catch(() => {});
  }

  get panel() {
    return this.querySelector(".nko-cart__panel");
  }

  /* D-CART-PAGE: `?cart=open` bzw. `#cart` oeffnet die Schublade. Vertrag
     siehe Dateikopf. */
  adresseAuswerten() {
    const u = new URL(location.href);
    const perParameter = u.searchParams.get("cart") === "open";
    const perAnker = u.hash === "#cart";
    if (!perParameter && !perAnker) return;
    if (perParameter) u.searchParams.delete("cart");
    if (perAnker) u.hash = "";
    history.replaceState(history.state, "", u.pathname + u.search + u.hash);
    this.oeffnen();
  }

  /* Haengt eine Aufgabe hinten an die Kette. Gibt ihr Ergebnis zurueck; ein
     Fehler bricht nur diese Aufgabe ab, nicht die Kette. */
  einreihen(aufgabe) {
    const lauf = this.kette.then(aufgabe);
    this.kette = lauf.catch(() => {});
    return lauf;
  }

  /* Rolle und `aria-modal` werden BEIM OEFFNEN gesetzt und beim Schliessen
     wieder entfernt, nicht fest ins Markup geschrieben: dauerhaft gesetzt
     hiess `aria-modal` fuer Screenreader, die ganze Seite auszublenden. */
  oeffnen() {
    if (!this.classList.contains("is-open"))
      this.ausloeserWar = document.activeElement;
    const panel = this.panel;
    if (panel) {
      clearTimeout(this.inertTimer);
      panel.inert = false;
      panel.setAttribute("role", "dialog");
      panel.setAttribute("aria-modal", "true");
    }
    this.expressEinhaengen();
    this.classList.add("is-open");
    // Sperrt das Scrollen der Seite (cart-drawer.css, P2-29).
    document.documentElement.classList.add("nko-cart-offen");
    this.querySelector(".nko-cart__close")?.focus({ preventScroll: true });
  }

  schliessen() {
    const panel = this.panel;
    if (panel) {
      panel.removeAttribute("role");
      panel.removeAttribute("aria-modal");
      /* Erst nach der Ausfahrt inert schalten, sonst verschwindet das Panel
         schlagartig statt herauszufahren. 300ms ist die Dauer aus
         cart-drawer.css (`transition: transform 0.3s ease`). */
      clearTimeout(this.inertTimer);
      this.inertTimer = setTimeout(() => {
        if (!this.classList.contains("is-open")) panel.inert = true;
      }, 300);
    }
    this.classList.remove("is-open");
    /* Die gemerkte Lage ein letztes Mal herstellen — VOR dem Entfernen der
       Klasse, solange sie das weiche Scrollen noch abschaltet. Ohne Vergleich
       mit `scrollY`: steht die Tastatur beim Schliessen noch, hat iOS die
       Seite noch nicht nachgezogen, der Aufruf richtet den verschobenen
       Ausschnitt aber schon wieder an ihr aus. */
    if (this.lageY != null) window.scrollTo({ top: this.lageY });
    this.lageY = null;
    document.documentElement.classList.remove("nko-cart-offen");
    this.meldungLeeren();

    /* Fokus zurueck auf den Ausloeser — aber nur, wenn er nicht inzwischen
       irgendwo GELANDET ist: ein Klick auf die Blende laesst den Fokus auf
       <body> fallen (dann zurueckholen), ein Klick auf einen Link ausserhalb
       setzt ihn auf ein echtes Ziel (dann in Ruhe lassen). */
    const jetzt = document.activeElement;
    const gelandet = jetzt && jetzt !== document.body && !this.contains(jetzt);
    if (this.ausloeserWar && !gelandet) {
      this.ausloeserWar.focus?.({ preventScroll: true });
    }
    this.ausloeserWar = null;
  }

  /* Stellt die beim Fokus gemerkte Lage der Seite wieder her (Begruendung in
     connectedCallback). Solange in einem Feld getippt wird, gehoert der
     Ausschnitt iOS — sonst stuende das Feld wieder hinter der Tastatur; wer
     hineingezoomt hat, verschiebt ihn selbst. Der Sprung ist hart, weil
     cart-drawer.css das weiche Scrollen abschaltet, solange die Schublade
     offen ist. Verglichen wird mit 1px Spiel: `scrollY` ist auf iOS
     gebrochen und landet nicht immer exakt auf dem gemerkten Wert. */
  lageHalten() {
    const feld = document.activeElement;
    if (
      this.lageY == null ||
      (this.contains(feld) && feld.matches("input, textarea"))
    )
      return;
    if (window.visualViewport?.scale > 1.01) return;
    if (Math.abs(window.scrollY - this.lageY) > 1)
      window.scrollTo({ top: this.lageY });
  }

  /* D-EXPRESS: die Express-Knoepfe stehen als <template> im Markup und
     werden beim ersten Oeffnen eingehaengt. Erst dann startet Shopify sie
     (Begruendung in sections/nko-cart-drawer.liquid). */
  expressEinhaengen() {
    const ziel = this.querySelector("[data-nko-cart-express]");
    const vorlage = ziel?.querySelector(":scope > template");
    if (!vorlage) return;
    ziel.append(vorlage.content.cloneNode(true));
    vorlage.remove();
  }

  /* Tab-Falle. Gleiches Muster wie popup.js:199-221. Steht der Fokus
     ausserhalb (etwa auf <body>, weil ein Element verschwand), holt Tab ihn
     zurueck in den Dialog statt auf die Seite dahinter. */
  falle(e) {
    const panel = this.panel;
    if (!panel) return;
    const ziele = [...panel.querySelectorAll(FOKUSSIERBAR)].filter(
      (el) => el.getClientRects().length > 0,
    );
    if (!ziele.length) return;
    const erste = ziele[0];
    const letzte = ziele[ziele.length - 1];
    const hier = document.activeElement;
    if (!panel.contains(hier)) {
      e.preventDefault();
      (e.shiftKey ? letzte : erste).focus();
    } else if (e.shiftKey && hier === erste) {
      e.preventDefault();
      letzte.focus();
    } else if (!e.shiftKey && hier === letzte) {
      e.preventDefault();
      erste.focus();
    }
  }

  /* ---- Fehler sichtbar machen ----

     Gezeigt wird, was Shopify sagt (P2-30): /cart/change.js, update.js und
     add.js liefern bei 422 eine uebersetzte `description` ("Du kannst nur 3 …
     hinzufuegen"). Vorher meldete jeder Fehler "Der Artikel konnte nicht in
     den Warenkorb gelegt werden" — auch beim Entfernen oder beim Rabatt.
     Der Text aus `data-label-cart-error` bleibt der Rueckfall.

     DAS ATTRIBUT HEISST BEWUSST ANDERS als in blocks/product-form.liquid:
     product-form.js sucht sein Ziel global mit `[data-nko-cart-error]`.

     Jede neue Aktion (Menge, Entfernen, Upsell, Rabatt) raeumt die Meldung
     der vorigen ab. Vorher leerte nur schliessen() sie: ein 422 blieb stehen,
     auch wenn danach alles glatt ging. Geleert wird beim Ausloesen, nicht
     nach dem Einbauen — sonst loeschte ein Tausch im Fehlerzweig (etwa beim
     Zuruecknehmen eines ungueltigen Rabattcodes) die gerade gesetzte
     Meldung. */
  melden(fehler) {
    const ziel = this.querySelector("[data-nko-drawer-error]");
    if (!ziel) return;
    ziel.textContent =
      (fehler && fehler.nutzertext) ||
      this.getAttribute("data-label-cart-error") ||
      "";
    ziel.hidden = false;
  }

  meldungLeeren() {
    const ziel = this.querySelector("[data-nko-drawer-error]");
    if (!ziel) return;
    ziel.hidden = true;
    ziel.textContent = "";
  }

  klick(e) {
    if (e.target.closest("[data-nko-cart-close]")) {
      e.preventDefault();
      this.schliessen();
      return;
    }

    const menge = e.target.closest("[data-nko-cart-qty]");
    if (menge) {
      this.mengeGeklickt(
        menge.getAttribute("data-nko-cart-qty"),
        Number(menge.getAttribute("data-delta")),
        menge.parentElement.querySelector(".nko-cart__qty-value"),
        menge.parentElement.querySelector("[data-max]"),
      );
      return;
    }

    /* Entfernen setzt die Zielmenge 0 und sendet sofort. Ein Set aus dem
       Konfigurator traegt alle Schluessel mit Komma (D-CONFIG-GROUP) und
       verschwindet nur als Ganzes. */
    const weg = e.target.closest("[data-nko-cart-remove]");
    if (weg) {
      this.meldungLeeren();
      const schluessel = weg
        .getAttribute("data-nko-cart-remove")
        .split(",")
        .filter(Boolean);
      for (const s of schluessel) this.wartend.set(s, 0);
      weg.closest(".nko-cart__item")?.classList.add("is-entfernt");
      clearTimeout(this.buendelTimer);
      this.einreihen(() => this.wartendeSenden()).catch(() => {});
      return;
    }

    const dazu = e.target.closest("[data-nko-cart-add]");
    if (dazu) {
      const id = Number(dazu.getAttribute("data-nko-cart-add"));
      if (!id || dazu.getAttribute("aria-busy") === "true") return;
      this.meldungLeeren();
      /* Sichtbar besetzt, bis die Anfrage durch ist — eingereiht wird sie
         trotzdem sofort, auch wenn gerade eine andere laeuft. */
      dazu.setAttribute("aria-busy", "true");
      this.einreihen(() => this.hinzufuegen(id)).finally(() => {
        dazu.removeAttribute("aria-busy");
      });
      return;
    }

    const codeWeg = e.target.closest("[data-nko-cart-discount-remove]");
    if (codeWeg) {
      const code = codeWeg.getAttribute("data-nko-cart-discount-remove");
      this.meldungLeeren();
      this.einreihen(() =>
        this.codesSenden(this.aktiveCodes().filter((c) => c !== code)),
      ).catch(() => {});
      return;
    }

    if (e.target.closest("[data-nko-cart-discount]")) {
      this.rabattAnwenden();
    }
  }

  /* Ein Mengenklick wirkt SOFORT in der Anzeige und wird gebuendelt
     abgeschickt. Die Merkliste haelt die Zielmenge je Zeile, damit mehrmaliges
     Tippen nicht mehrmals sendet — und nichts verloren geht.

     `data-max` am Plus-Knopf ist der Bestand laut Liquid (nur bei Varianten,
     die Shopify verfolgt und nicht ueberverkauft). Darueber zaehlt die
     Anzeige nicht hoch, sondern meldet sofort. Das ist nur Komfort: der
     Bestand kann seit dem Rendern gesunken sein, entscheidend bleibt die
     Pruefung durch change.js. */
  mengeGeklickt(schluessel, delta, anzeige, plus) {
    this.meldungLeeren();
    const basis = this.wartend.has(schluessel)
      ? this.wartend.get(schluessel)
      : Number(anzeige?.textContent.trim() || 0);
    const max = plus ? Number(plus.getAttribute("data-max")) : Infinity;
    if (delta > 0 && basis + delta > max) {
      this.mengeBegrenzt();
      return;
    }
    const ziel = Math.max(0, basis + delta);

    this.wartend.set(schluessel, ziel);
    this.zeigeWartend();

    clearTimeout(this.buendelTimer);
    this.buendelTimer = setTimeout(() => {
      this.einreihen(() => this.wartendeSenden()).catch(() => {});
    }, 450);
  }

  /* Schreibt die noch nicht bestaetigten Zielmengen in die Anzeige — nach
     einem Klick und nach jedem Tausch, damit ein Tausch waehrend des
     Tippens die Anzeige nicht auf den alten Stand zuruecksetzt. Auf 0 wird
     die Zeile schon ausgegraut. */
  zeigeWartend() {
    for (const [schluessel, ziel] of this.wartend) {
      const zeile = this.querySelector(
        `[data-nko-cart-line="${CSS.escape(schluessel)}"]`,
      );
      if (!zeile) continue;
      const anzeige = zeile.querySelector(".nko-cart__qty-value");
      if (anzeige) anzeige.textContent = ziel;
      const plus = zeile.querySelector("[data-max]");
      if (plus) {
        plus.setAttribute(
          "aria-disabled",
          String(ziel >= Number(plus.getAttribute("data-max"))),
        );
      }
      zeile.classList.toggle("is-entfernt", ziel === 0);
    }
  }

  /* WARUM change.js UND NICHT update.js FUER MENGEN
     Shopify: "The update.js endpoint doesn't validate the quantity on
     variants that are already in the cart." Mit update.js liess sich jede
     Zeile ueber den Bestand hochtippen, erst der Checkout lehnte ab.
     change.js prueft den Bestand: es antwortet mit 422 und Shopifys
     uebersetztem Text oder kappt auf das Verfuegbare. Es nimmt nur EINE
     Zeile je Anfrage — die Zeilen laufen deshalb nacheinander, meist ist es
     ohnehin eine. Entfernen (Menge 0) bleibt EIN update.js: der Bestand
     spielt keine Rolle, und ein Set verschwindet so in einer Anfrage als
     Ganzes. */
  async wartendeSenden() {
    const posten = [...this.wartend.entries()];
    if (!posten.length) return;
    const weg = posten.filter(([, menge]) => menge === 0);
    const mengen = posten.filter(([, menge]) => menge > 0);
    let antwort;
    let gekappt = false;
    try {
      if (weg.length) {
        antwort = await this.cartFetch("cart/update.js", {
          updates: Object.fromEntries(weg),
        });
      }
      for (const [schluessel, menge] of mengen) {
        antwort = await this.cartFetch("cart/change.js", {
          id: schluessel,
          quantity: menge,
        });
        const zeile = (antwort.items || []).find((i) => i.key === schluessel);
        if (zeile && zeile.quantity < menge) gekappt = true;
      }
    } catch (fehler) {
      for (const [schluessel] of posten) this.wartend.delete(schluessel);
      /* Keine Konsolenausgabe (Architektur §5.1): der Kunde sieht die Meldung
         in der Schublade, das reicht. */
      await this.aktualisieren().catch(() =>
        this.mengenZuruecksetzen(posten).catch(() => {}),
      );
      this.melden(fehler);
      return;
    }
    /* Nur austragen, was unveraendert gesendet wurde. Wer waehrenddessen
       weitergetippt hat, hat einen neuen Zielwert — der geht mit der
       naechsten Runde. */
    for (const [schluessel, menge] of posten) {
      if (this.wartend.get(schluessel) === menge)
        this.wartend.delete(schluessel);
    }
    await this.einbauenOderNeuLaden(antwort.sections?.[this.sektion]);
    // Gekappt statt abgelehnt: das Markup zeigt schon die echte Menge.
    if (gekappt) this.mengeBegrenzt();
  }

  mengeBegrenzt() {
    this.melden({ nutzertext: this.getAttribute("data-label-qty-max") || "" });
  }

  /* Rueckfall, wenn nach einer abgelehnten Aenderung auch das Nachholen der
     Section scheitert: die Anzeige zaehlte schon hoch (etwa 14 bei Bestand
     12), der Warenkorb nicht. Die echten Mengen aus /cart.js zuruecksetzen —
     die Meldung bleibt stehen, anders als bei neuLadenOffen(). Gefunden am
     Entwicklungs-Server, der `?section_id=` mit 404 beantwortet. */
  async mengenZuruecksetzen(posten) {
    const warenkorb = await fetch(`${WURZEL}cart.js`).then((r) => r.json());
    for (const [schluessel] of posten) {
      const zeile = this.querySelector(
        `[data-nko-cart-line="${CSS.escape(schluessel)}"]`,
      );
      const echt = warenkorb.items.find((i) => i.key === schluessel);
      if (!zeile || !echt) continue;
      const anzeige = zeile.querySelector(".nko-cart__qty-value");
      if (anzeige) anzeige.textContent = echt.quantity;
      zeile.classList.remove("is-entfernt");
    }
  }

  async hinzufuegen(id) {
    let antwort;
    try {
      antwort = await this.cartFetch("cart/add.js", {
        items: [{ id, quantity: 1 }],
      });
    } catch (fehler) {
      this.melden(fehler);
      return;
    }
    await this.einbauenOderNeuLaden(antwort.sections?.[this.sektion]);
  }

  /* DER WARENKORB IST SCHON GEAENDERT, nur das neue Markup fehlt (Shopify
     lieferte `sections: { id: null }`, und auch das Nachholen per GET
     scheiterte). Vorher lief das in denselben Fehlerzweig wie eine
     abgelehnte Aenderung: die Schublade zeigte den ALTEN Stand und „Der
     Artikel konnte nicht in den Warenkorb gelegt werden“ — wer daraufhin noch
     einmal tippte, aenderte die Menge ein zweites Mal (Nachpruefung am
     Simulator 06.10.2026, 9 von 16 Mengenaenderungen, als der
     Entwicklungs-Server die Section-Abfragen mit 404 beantwortete). Dann
     lieber die Seite mit `?cart=open` neu laden: der Server rendert die
     Schublade mit dem echten Stand, adresseAuswerten() oeffnet sie. */
  async einbauenOderNeuLaden(html) {
    try {
      await this.einbauen(html);
    } catch (_) {
      this.neuLadenOffen();
    }
  }

  neuLadenOffen() {
    const u = new URL(location.href);
    u.searchParams.set("cart", "open");
    location.replace(u);
  }

  /* ---- Rabattcodes (P2-19) ----

     `discount` an /cart/update.js ist der Rabatt-Parameter der Ajax-API:
     Shopify prueft den Code, verrechnet ihn und traegt ihn in
     `cart.discount_codes` ein, mit `applicable`. Der Parameter ERSETZT die
     Liste — deshalb gehen die schon aktiven Codes (`data-codes` am Rabattblock)
     jedes Mal mit. Einen Code, den Shopify nicht anwendet, nimmt das Skript
     wieder heraus und meldet "Bitte einen gueltigen Code eingeben", wie die
     App im Laden. Die Bestaetigung ("Rabatt von … angewendet") und die
     Rabattzeile im Fuss rendert Liquid. */
  aktiveCodes() {
    const roh =
      this.querySelector(".nko-cart__discount")?.getAttribute("data-codes") ||
      "";
    return roh.split(",").filter(Boolean);
  }

  rabattAnwenden() {
    const feld = this.querySelector(".nko-cart__discount-input");
    const code = (feld?.value || "").trim();
    if (!code) return;
    this.meldungLeeren();
    this.einreihen(async () => {
      const vorher = this.aktiveCodes();
      if (vorher.some((c) => c.toLowerCase() === code.toLowerCase())) {
        if (feld) feld.value = "";
        return;
      }
      const antwort = await this.codesSenden([...vorher, code], {
        still: true,
      });
      if (!antwort) return;
      const eintrag = (antwort.discount_codes || []).find(
        (d) => d.code.toLowerCase() === code.toLowerCase(),
      );
      if (eintrag && eintrag.applicable) {
        // Angewendet: das Feld leert sich, die Bestaetigung kommt aus Liquid.
        const eingabe = this.querySelector(".nko-cart__discount-input");
        if (eingabe) eingabe.value = "";
        await this.einbauen(antwort.sections?.[this.sektion]);
        return;
      }
      // Nicht anwendbar: wieder heraus, dann die Meldung ins neue Markup.
      const zurueck = await this.codesSenden(vorher, { still: true });
      await this.einbauen((zurueck || antwort).sections?.[this.sektion]);
      const notiz = this.querySelector("[data-nko-cart-discount-note]");
      const eingabe = this.querySelector(".nko-cart__discount-input");
      if (eingabe) eingabe.value = code;
      if (notiz) {
        notiz.textContent =
          this.getAttribute("data-label-discount-invalid") || "";
        notiz.hidden = false;
      }
    }).catch((fehler) => this.melden(fehler));
  }

  async codesSenden(codes, { still = false } = {}) {
    const koerper = { discount: codes.join(",") };
    /* Altlast der ersten Fassung: der Code als Warenkorb-Attribut. Einmal mit
       abraeumen, sonst landet er als Notiz in der Bestellung. */
    if (this.hasAttribute("data-alt-rabatt")) {
      koerper.attributes = { discount: "" };
      this.removeAttribute("data-alt-rabatt");
    }
    try {
      const antwort = await this.cartFetch("cart/update.js", koerper);
      if (!still) await this.einbauen(antwort.sections?.[this.sektion]);
      return antwort;
    } catch (fehler) {
      this.melden(fehler);
      return null;
    }
  }

  /* POST an die Ajax-API, IMMER mit `sections`: die Antwort traegt dann das
     neu gerenderte Markup dieser Section unter `sections[<id>]`.

     `sections_url` ist die Startseite der aktuellen Sprache bzw. des Markts,
     nicht `location.pathname` — auf einer 404-Seite scheitert das Rendern
     sonst. Bei einem Fehler wird der JSON-Koerper gelesen und Shopifys
     uebersetzte `description` weitergereicht. */
  async cartFetch(pfad, koerper) {
    const antwort = await fetch(`${WURZEL}${pfad}`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
      },
      body: JSON.stringify({
        ...koerper,
        sections: this.sektion,
        sections_url: WURZEL,
      }),
    });
    if (!antwort.ok) {
      let text = "";
      try {
        const daten = await antwort.json();
        text = daten.description || daten.message || "";
      } catch (_) {
        /* kein JSON — dann bleibt der allgemeine Text */
      }
      const fehler = new Error(`${pfad} antwortete ${antwort.status}`);
      fehler.nutzertext = typeof text === "string" ? text : "";
      throw fehler;
    }
    return antwort.json();
  }

  /* Rueckfall: die Section per GET holen. */
  async aktualisieren() {
    if (!this.sektion) return;
    const antwort = await fetch(
      `${WURZEL}?section_id=${encodeURIComponent(this.sektion)}`,
    );
    if (!antwort.ok) throw new Error(`section_id antwortete ${antwort.status}`);
    await this.einbauen(await antwort.text());
  }

  /* Baut neu gerendertes Section-Markup ein. Ohne HTML (Rendern gescheitert)
     wird per GET nachgeholt. */
  async einbauen(html) {
    if (!html) return this.aktualisieren();
    const doc = new DOMParser().parseFromString(html, "text/html");

    // Was der Tausch zerstoert, vorher merken: Fokus und angefangene Eingabe.
    const fokus = this.fokusMerken();
    const rabattText =
      this.querySelector(".nko-cart__discount-input")?.value || "";

    for (const wahl of [
      "[data-nko-cart-body]",
      "[data-nko-cart-summe]",
      ".nko-cart__title",
    ]) {
      const alt = this.querySelector(wahl);
      const neu = doc.querySelector(wahl);
      if (alt && neu && alt.innerHTML !== neu.innerHTML)
        alt.innerHTML = neu.innerHTML;
    }

    const quelle = doc.querySelector("nko-cart-drawer");
    const neuerZaehler = quelle?.getAttribute("data-count");
    if (neuerZaehler != null) {
      this.setAttribute("data-count", neuerZaehler);
      for (const z of document.querySelectorAll("[data-cart-count]"))
        z.textContent = neuerZaehler;
    }
    if (quelle && !quelle.hasAttribute("data-alt-rabatt"))
      this.removeAttribute("data-alt-rabatt");

    const eingabe = this.querySelector(".nko-cart__discount-input");
    if (eingabe && rabattText && !eingabe.value) eingabe.value = rabattText;

    this.zeigeWartend();
    this.fokusZurueck(fokus);
  }

  fokusMerken() {
    const el = document.activeElement;
    if (!el || !this.contains(el) || el.closest(".nko-cart__head")) return null;
    for (const name of FOKUS_MERKMALE) {
      if (!el.hasAttribute(name)) continue;
      let wahl = `[${name}="${CSS.escape(el.getAttribute(name))}"]`;
      if (name === "id") wahl = `#${CSS.escape(el.id)}`;
      if (el.hasAttribute("data-delta"))
        wahl += `[data-delta="${el.getAttribute("data-delta")}"]`;
      return wahl;
    }
    return "";
  }

  /* Dasselbe Element im neuen Markup fokussieren; gibt es es nicht mehr (Zeile
     entfernt), den Schliessen-Knopf — Hauptsache, der Fokus bleibt im
     Dialog. */
  fokusZurueck(wahl) {
    if (wahl === null || !this.classList.contains("is-open")) return;
    const ziel =
      (wahl && this.querySelector(wahl)) ||
      this.querySelector(".nko-cart__close");
    ziel?.focus({ preventScroll: true });
  }
}

if (!customElements.get("nko-cart-drawer")) {
  customElements.define("nko-cart-drawer", NkoCartDrawer);
}

/* ----------------------------------------------------------------------------
   NUR ALS MODUL LADEN: <script src="…" type="module">, nie mit `defer`.

   Diese Datei wird ungebaut ausgeliefert. Frueher packte esbuild sie in eine
   IIFE; diesen eigenen Gueltigkeitsbereich gibt ihr jetzt der Modulbereich.
   Als klassisches Skript laegen alle Namen oben global und stiessen sich an
   denen der anderen Theme-Dateien (`FOKUSSIERBAR` steht in zwei, `starte` in
   mehreren), und `open` aus popup.js ueberschriebe `window.open`. Module
   laufen wie `defer` nach dem Parsen in Dokumentreihenfolge, aber nur einmal
   je URL und immer im strict mode.
   ---------------------------------------------------------------------------- */
